// src/lib/clientParseFields.js — what a parsed client message may change, and how it is compared.
//
// OWNER, 2026-09-04, describing the flow this exists for: "paste text, parse, save, come back,
// paste another text for more details, parse, enrich — if different values ask user which to keep."
//
// The supply side got that rule on 2026-09-02 (T40, `parseConflicts.js` + `ParseConflictPanel`).
// The demand side never did: `ManualClientModal.handlePasteParse` wrote `p.phone || prev.phone`
// and `{ ...prev, ...cmd.criteria }`, so the newest parse won every field it mentioned — over the
// previous parse AND over anything the agent had typed. This module is the demand-side half, and
// it is ONE list read by both client surfaces (the add modal and the saved-demand edit modal) so
// the two cannot drift — the disease `deskFilters.js` was built to end.
//
// ── WHY A `cmp` SEPARATE FROM A `show` ────────────────────────────────────────────────────────
// `splitParse` uses one value for both the comparison and the panel's text. That is right for a
// price and wrong for everything structured here: a set of district codes has no order (sorting
// before comparing stops "vake,saburtalo" → "saburtalo,vake" being asked as a change), and a
// drawn polygon compared as an object stringifies to `[object Object]` — the same defect that put
// `[object Object], ,გზის დახაზვა` on the road picker's buttons the day before this was written.
// So each field declares:
//   cmp(v)  → the canonical string the DECISION is made on (exact, order-insensitive for sets)
//   show(v) → what a human reads in the panel ("3 districts", "area — 5 points")
// and the value actually applied is the RAW one from the parse, never either string.
//
// ⚠️ A field absent from this list is NOT applied by a parse at all. That is the safe direction
// (the agent's value stands) but it is also silent, so add the field here rather than reaching
// past the list.
import { t } from './i18n.js'
import { splitParse } from './parseConflicts.js'

const asArray = v => (Array.isArray(v) ? v : v == null ? [] : [v])
// Sets: order is not an answer. Sorted so re-parsing the same three districts in a different
// order is agreement, not a question the agent has to read and dismiss.
const cmpSet = v => { const a = asArray(v).map(x => String(x).trim()).filter(Boolean); return a.length ? a.slice().sort().join('|') : '' }
const showSet = v => { const a = asArray(v); return a.length ? a.join(', ') : '' }
// Booleans are TRI-STATE, and the third state is the one that matters. The form writes `true`
// or `null` (unticking is not a recorded "no"), but a SAVED record can hold an explicit `false` —
// the fill form asks the client "any pets?" and stores their no. Found on a live client
// (6f39d6c8, has_pets=false) while testing this on production: reading false as absence let a
// message saying the client HAS a pet flip a recorded no, silently, which is the exact defect
// this module exists to end. null/undefined = unanswered (fill); false = "no" (ask before
// replacing); true = "yes".
const cmpBool = v => (v === true || v === 'true' ? 'yes' : v === false || v === 'false' ? 'no' : '')
const showBool = v => { const c = cmpBool(v); return c === 'yes' ? t('desk_parse_value_yes') : c === 'no' ? t('desk_parse_value_no') : '' }
const cmpJson = v => (v == null ? '' : JSON.stringify(v))

// near_points is a list of resolved places; the panel names them rather than dumping objects.
const nearNames = v => asArray(v).map(p => p?.label || p?.name || p?.code || '?').filter(Boolean)

/**
 * bucket: which state object the value belongs to — 'party' | 'reqInfo' | 'criteria'.
 * read(cmd): pull the value out of an extract_requirement command.
 * Labels are functions because i18n is read at render, not at module load.
 */
export const CLIENT_PARSE_FIELDS = [
  // ── person ────────────────────────────────────────────────────────────────────────────────
  // "Unknown contact" is normalizeEnvelope's placeholder for a nameless message and must never
  // be offered as a replacement for a name the agent already has.
  { key: 'name',  bucket: 'party', label: () => t('desk_client_name_label'),
    read: c => (c.person?.display_name && c.person.display_name !== 'Unknown contact' ? c.person.display_name : null) },
  { key: 'phone', bucket: 'party', label: () => t('desk_client_phone'), read: c => c.person?.phone },
  { key: 'email', bucket: 'party', label: () => t('desk_client_email'), read: c => c.person?.email },

  // ── the requirement's own fields ──────────────────────────────────────────────────────────
  { key: 'intent',            bucket: 'reqInfo', label: () => t('desk_client_looking_to'),    read: c => c.requirement?.intent },
  { key: 'property_category', bucket: 'reqInfo', label: () => t('desk_client_property_type'), read: c => c.requirement?.property_category },
  { key: 'urgency',           bucket: 'reqInfo', label: () => t('desk_client_urgency'),       read: c => c.requirement?.urgency },
  { key: 'title',             bucket: 'reqInfo', label: () => t('desk_review_section_intent'), read: c => c.requirement?.title },

  // ── criteria: plain numbers, dates and enums ──────────────────────────────────────────────
  { key: 'price_min',          bucket: 'criteria', label: () => t('desk_criteria_price_min') },
  { key: 'price_max',          bucket: 'criteria', label: () => t('desk_criteria_price_max') },
  { key: 'currency',           bucket: 'criteria', label: () => t('desk_criteria_currency') },
  { key: 'max_drive_minutes',  bucket: 'criteria', label: () => t('desk_criteria_max_drive') },
  { key: 'area_sqm_min',       bucket: 'criteria', label: () => t('desk_criteria_area_min') },
  { key: 'area_sqm_max',       bucket: 'criteria', label: () => t('desk_criteria_area_max') },
  { key: 'land_sqm_min',       bucket: 'criteria', label: () => t('desk_criteria_land_min') },
  { key: 'land_sqm_max',       bucket: 'criteria', label: () => t('desk_criteria_land_max') },
  { key: 'required_power_kw',  bucket: 'criteria', label: () => t('desk_criteria_power_min') },
  { key: 'rooms_min',          bucket: 'criteria', label: () => t('desk_criteria_rooms_min') },
  { key: 'rooms_max',          bucket: 'criteria', label: () => t('desk_criteria_rooms_max') },
  { key: 'bedrooms_min',       bucket: 'criteria', label: () => t('desk_criteria_bedrooms_min') },
  { key: 'bedrooms_max',       bucket: 'criteria', label: () => t('desk_criteria_bedrooms_max') },
  { key: 'floor_min',          bucket: 'criteria', label: () => t('desk_criteria_floor_min') },
  { key: 'floor_max',          bucket: 'criteria', label: () => t('desk_criteria_floor_max') },
  { key: 'occupants',          bucket: 'criteria', label: () => t('desk_criteria_occupants') },
  { key: 'max_deposit_months', bucket: 'criteria', label: () => t('desk_crit_max_deposit_months') },
  { key: 'prepayment_months',  bucket: 'criteria', label: () => t('desk_crit_prepayment_months') },
  { key: 'move_in_date',       bucket: 'criteria', label: () => t('desk_criteria_move_in') },
  { key: 'check_out_date',     bucket: 'criteria', label: () => t('desk_criteria_check_out') },

  // ── criteria: ticks ───────────────────────────────────────────────────────────────────────
  { key: 'floor_not_first',      bucket: 'criteria', label: () => t('desk_criteria_floor_not_first'), cmp: cmpBool, show: showBool },
  { key: 'floor_not_last',       bucket: 'criteria', label: () => t('desk_criteria_floor_not_last'),  cmp: cmpBool, show: showBool },
  { key: 'has_pets',             bucket: 'criteria', label: () => t('desk_crit_has_pets'),            cmp: cmpBool, show: showBool },
  { key: 'has_children',         bucket: 'criteria', label: () => t('desk_crit_has_children'),        cmp: cmpBool, show: showBool },
  { key: 'mortgage_ready',       bucket: 'criteria', label: () => t('desk_crit_mortgage_ready'),      cmp: cmpBool, show: showBool },
  { key: 'needs_deeded_parking', bucket: 'criteria', label: () => t('desk_crit_parking_deeded'),      cmp: cmpBool, show: showBool },

  // ── criteria: sets ────────────────────────────────────────────────────────────────────────
  { key: 'district_must_codes',     bucket: 'criteria', label: () => t('desk_parse_field_districts_must'), cmp: cmpSet, show: showSet },
  { key: 'district_codes',          bucket: 'criteria', label: () => t('desk_parse_field_districts_nice'), cmp: cmpSet, show: showSet },
  { key: 'excluded_district_codes', bucket: 'criteria', label: () => t('desk_parse_field_districts_not'),  cmp: cmpSet, show: showSet },
  { key: 'must_have_codes',         bucket: 'criteria', label: () => t('desk_parse_field_features_must'),  cmp: cmpSet, show: showSet },
  { key: 'nice_to_have_codes',      bucket: 'criteria', label: () => t('desk_parse_field_features_nice'),  cmp: cmpSet, show: showSet },
  { key: 'must_not_have_codes',     bucket: 'criteria', label: () => t('desk_parse_field_features_not'),   cmp: cmpSet, show: showSet },
  { key: 'condition_codes',         bucket: 'criteria', label: () => t('desk_criteria_section_quality'),   cmp: cmpSet, show: showSet },
  { key: 'complex_codes',           bucket: 'criteria', label: () => t('desk_complex_label'),              cmp: cmpSet, show: showSet },
  { key: 'household_codes',         bucket: 'criteria', label: () => t('desk_criteria_household_tags'),    cmp: cmpSet, show: showSet },
  { key: 'children_ages',           bucket: 'criteria', label: () => t('desk_criteria_children_ages'),     cmp: cmpSet, show: showSet },

  // ── criteria: structured ──────────────────────────────────────────────────────────────────
  // Named, not dumped: a list of place objects rendered raw is `[object Object]`.
  { key: 'near_points', bucket: 'criteria', label: () => t('desk_near_label'),
    cmp: v => cmpSet(nearNames(v)), show: v => showSet(nearNames(v)) },
  // A polygon is DRAWN, not written, so a parse can only ever fill an empty one here: two
  // different areas compare by their exact geometry and the agent is asked before one replaces
  // the other, but the panel shows a point count because no one can read a coordinate list.
  { key: 'search_polygon', bucket: 'criteria', label: () => t('desk_criteria_search_area'),
    cmp: cmpJson,
    show: v => (v == null ? '' : t('desk_parse_value_drawn_area')) },
]

const byKey = new Map(CLIENT_PARSE_FIELDS.map(f => [f.key, f]))
const readField = (f, cmd) => (f.read ? f.read(cmd) : cmd.criteria?.[f.key])
const cmpOf = (f, v) => (f.cmp ? f.cmp(v) : v)
const showOf = (f, v) => (f.show ? f.show(v) : v)

/**
 * Decide what a parsed command may write, given what is on the form now.
 *
 * @param cmd      an `extract_requirement` (or `amend_requirement`) command
 * @param current  { party, reqInfo, criteria } — whatever the surface holds; a missing bucket
 *                 means that surface does not edit those fields, and they are skipped entirely
 *                 rather than silently applied somewhere they cannot be seen.
 * @returns { patch, conflicts }
 *   patch     — { party:{...}, reqInfo:{...}, criteria:{...} }, RAW values, safe to write now
 *   conflicts — panel rows { key, label, from, to } (display strings) plus `raw` for applying
 */
export function splitClientParse(cmd, current = {}) {
  const fields = [], raw = new Map()
  for (const f of CLIENT_PARSE_FIELDS) {
    const bucket = current[f.bucket]
    if (!bucket) continue                       // this surface does not edit that bucket
    const incoming = readField(f, cmd || {})
    raw.set(f.key, incoming)
    fields.push({ key: f.key, label: f.label(), current: cmpOf(f, bucket[f.key]), incoming: cmpOf(f, incoming) })
  }
  const { apply, conflicts } = splitParse(fields)
  return {
    patch: patchFor(Object.keys(apply), raw, current),
    conflicts: conflicts.map(c => {
      const f = byKey.get(c.key)
      const bucket = current[f.bucket]
      return { key: c.key, label: c.label, from: showOf(f, bucket[f.key]), to: showOf(f, raw.get(c.key)) }
    }),
    raw,
  }
}

/** The keys the agent said yes to, turned into the same bucketed patch shape. */
export function acceptedClientPatch(conflicts, answers, raw, current = {}) {
  const keys = (conflicts || []).filter(c => answers?.[c.key] === true).map(c => c.key)
  return patchFor(keys, raw, current)
}

function patchFor(keys, raw, current) {
  const out = {}
  for (const key of keys) {
    const f = byKey.get(key)
    if (!f || !current[f.bucket]) continue
    ;(out[f.bucket] ||= {})[key] = raw.get(key)
  }
  return out
}
